/**
 * Request bodies the API accepts. The API validates with these schemas, and
 * the web and mobile apps use the same ones for their forms.
 */
import { z } from 'zod';
// ---- Building blocks ---------------------------------------------------------
export const roles = ['TENANT_ADMIN', 'REGION_MANAGER', 'AGENT'];
export const roleSchema = z.enum(roles);
export const recordingNotices = ['NONE', 'ANNOUNCE', 'REMIND'];
export const recordingNoticeSchema = z.enum(recordingNotices);
export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254));
/** Long passphrases rather than complexity rules. */
export const passwordSchema = z.string().min(12, 'Use at least 12 characters').max(200);
export const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a colour like #2459D6');
export const hostnameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/, 'Not a valid hostname');
/** International format, e.g. +37061234567. */
export const e164Schema = z.string().regex(/^\+[1-9]\d{6,14}$/, 'Use the international format, e.g. +37061234567');
/**
 * Emergency numbers in the countries we serve or plan to (EU 112, UK 999,
 * the Baltic short numbers, Poland, Italy) and 911. Not usable as extensions;
 * what happens when someone dials one is not decided yet.
 */
export const EMERGENCY_NUMBERS = new Set([
    '112', '999', '911', '01', '02', '03', '04', '033', '110', '113', '115', '118', '997', '998',
]);
/** A whole number in a range, with one message for every way it can be wrong. */
const wholeNumber = (min, max, unit) => {
    const message = `Use ${min} to ${max} ${unit}`;
    return z.number(message).int(message).min(min, message).max(max, message);
};
export const extensionSchema = z
    .string()
    .regex(/^\d{2,6}$/, 'Use 2 to 6 digits')
    .refine((value) => !EMERGENCY_NUMBERS.has(value), 'That is an emergency number');
export const countryCodeSchema = z.string().regex(/^[A-Z]{2}$/, 'Use a two-letter country code, e.g. LT');
export const languageSchema = z.string().regex(/^[a-z]{2}$/, 'Use a two-letter language code, e.g. lt');
export const timeZoneSchema = z.string().refine((zone) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: zone });
        return true;
    }
    catch {
        return false;
    }
}, 'Not a known time zone');
export const totpCodeSchema = z.string().regex(/^\d{6}$/, 'Enter the 6-digit code');
const nameSchema = z.string().trim().min(1).max(120);
// ---- Sign-in ------------------------------------------------------------------
export const signInSchema = z.object({ email: emailSchema, password: z.string().min(1).max(200) });
export const mfaSchema = z.object({ code: totpCodeSchema });
export const setPasswordSchema = z.object({ token: z.string().min(20).max(200), password: passwordSchema });
export const changePasswordSchema = z.object({ currentPassword: z.string().min(1).max(200), newPassword: passwordSchema });
// ---- Tenant administration -------------------------------------------------------
export const createUserSchema = z.object({
    name: nameSchema,
    email: emailSchema,
    role: roleSchema,
    regionId: z.uuid().nullable(),
    extension: extensionSchema,
    mobileNumber: e164Schema.nullable(),
});
export const updateUserSchema = createUserSchema.partial().extend({ disabled: z.boolean().optional() });
export const regionSchema = z.object({
    name: nameSchema,
    countryCode: countryCodeSchema,
    timeZone: timeZoneSchema,
    languages: z.array(languageSchema).min(1, 'Add at least one language').max(10),
    recordingNotice: recordingNoticeSchema,
    retentionDays: wholeNumber(1, 3650, 'days'),
    allowedDestinationCountries: z.array(countryCodeSchema).max(250),
});
export const brandingSchema = z.object({
    productName: nameSchema,
    primaryColor: hexColorSchema,
    accentColor: hexColorSchema,
    emailSenderName: nameSchema,
});
export const brandingAssetKinds = ['logo', 'favicon'];
export const brandingImageTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'image/x-icon'];
export const maxBrandingImageBytes = 512 * 1024;
/** A table downloaded as an Excel file (made in the browser): for the audit log. */
export const tableExportSchema = z.object({
    table: z.string().regex(/^[a-z-]{1,40}$/),
    rows: z.number().int().min(0).max(1_000_000),
    columns: z.array(z.string().regex(/^[a-zA-Z-]{1,40}$/)).max(60),
});
/** A person's photo: what a phone's camera saves; the server makes it a small square. */
export const userPhotoTypes = ['image/png', 'image/jpeg', 'image/webp'];
export const maxUserPhotoBytes = 15 * 1024 * 1024;
// ---- Softphone ------------------------------------------------------------------
export const softphoneCredentialsSchema = z.object({
    /** The device this browser or phone used before, if any. */
    deviceId: z.uuid().nullable(),
    label: z.string().trim().min(1).max(120),
});
// ---- Platform console ----------------------------------------------------------------
export const createTenantSchema = z.object({
    name: nameSchema,
    hostname: hostnameSchema,
    timeZone: timeZoneSchema,
    branding: brandingSchema,
    maxConcurrentCalls: z.number().int().min(1).max(10_000),
    dailyMinutesCap: z.number().int().min(1).max(10_000_000),
    /** AI call analysis: tokens per month. Reviews stop when it is used up. */
    aiMonthlyTokenBudget: z.number().int().min(0).max(1_000_000_000).optional(),
});
export const updateTenantSchema = createTenantSchema.partial();
export const createTenantAdminSchema = z.object({ name: nameSchema, email: emailSchema, extension: extensionSchema });
// ---- Carriers, numbers and call settings ---------------------------------------------------
export const trunkAuthTypes = ['REGISTRATION', 'IP'];
export const numberFormats = ['E164', 'E164_NO_PLUS', 'INTERNATIONAL_00'];
const hostSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?(:\d{1,5})?$/, 'Use a hostname or address, with :port if not 5060');
const domainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(/^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/, 'Use a domain or address, e.g. 203.0.113.10');
const ipv4OrRangeSchema = z
    .string()
    .trim()
    .regex(/^((25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(25[0-5]|2[0-4]\d|1?\d?\d)(\/(3[0-2]|[12]?\d))?$/, 'Use an IPv4 address or range, e.g. 185.45.152.0/24');
export const trunkSchema = z
    .object({
    name: nameSchema,
    countryCode: countryCodeSchema,
    provider: nameSchema,
    authType: z.enum(trunkAuthTypes),
    host: hostSchema,
    /** Registration-style trunks: the account name. */
    username: z.string().trim().max(120).nullable(),
    /** Write-only. Null or absent keeps the current password. */
    password: z.string().max(200).nullable().optional(),
    /** IP-style trunks: the carrier's addresses that send our calls. */
    inboundAddresses: z.array(ipv4OrRangeSchema).max(50),
    numberFormat: z.enum(numberFormats),
    fromUser: z.string().trim().max(120).nullable(),
    /** The domain in From, when the carrier asks for one (e.g. this server's address). */
    fromDomain: domainSchema.nullable().optional(),
    /** Where sign-in and calls are sent, when the carrier gives a proxy apart from its server (domain). */
    outboundProxy: hostSchema.nullable().optional(),
    /** The carrier passes texts (SMS) as SIP MESSAGE requests. */
    texts: z.boolean().default(false),
})
    .superRefine((trunk, ctx) => {
    if (trunk.authType === 'REGISTRATION' && !trunk.username) {
        ctx.addIssue({ code: 'custom', path: ['username'], message: 'Enter the account name' });
    }
    if (trunk.authType === 'IP' && trunk.inboundAddresses.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['inboundAddresses'], message: 'Add the carrier’s addresses' });
    }
});
export const phoneNumberSchema = z.object({
    e164: e164Schema,
    trunkId: z.uuid(),
    regionId: z.uuid(),
    /** The person this number rings, or null for a shared number of the region. */
    userId: z.uuid().nullable(),
    /** Or the queue it leads to, or the call flow it starts (one of them). */
    queueId: z.uuid().nullable().default(null),
    flowId: z.uuid().nullable().default(null),
    label: z.string().trim().max(120).nullable(),
    /** Texts to and from it (its carrier must pass them). */
    texts: z.boolean().default(false),
    /** Texts go to this queue; null: where its calls go. */
    textQueueId: z.uuid().nullable().default(null),
});
export const callSettingsSchema = z.object({
    confirmMobileCalls: z.boolean(),
    ringSeconds: wholeNumber(5, 120, 'seconds'),
    mobileRingSeconds: wholeNumber(5, 120, 'seconds'),
    wakeSeconds: wholeNumber(3, 60, 'seconds'),
    maxCallMinutes: wholeNumber(5, 1440, 'minutes'),
    /** Each person's limits: calls at once, and minutes of calls out per day. */
    userMaxConcurrentCalls: wholeNumber(1, 10, 'calls'),
    userDailyMinutes: wholeNumber(1, 1440, 'minutes'),
    /** And texts sent per day. */
    userDailyTexts: wholeNumber(1, 1000, 'texts'),
});
export const clickToCallSchema = z.object({ number: z.string().trim().min(2).max(32) });
// ---- Call history, dashboard and transcription ---------------------------------------
export const callOutcomes = ['ANSWERED', 'MISSED', 'VOICEMAIL', 'NO_ANSWER', 'BUSY', 'FAILED', 'REJECTED', 'CANCELLED'];
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
export const callQuerySchema = z.object({
    direction: z.enum(['INBOUND', 'OUTBOUND', 'INTERNAL']).optional(),
    outcome: z.enum(callOutcomes).optional(),
    userId: z.uuid().optional(),
    regionId: z.uuid().optional(),
    /** Local dates, inclusive. */
    from: isoDate.optional(),
    to: isoDate.optional(),
    /** A number (digits) or words said in the call. */
    q: z.string().trim().max(200).optional(),
    /** Calls whose review gives this reason (a reason category). */
    reasonCategoryId: z.uuid().optional(),
    saleOutcome: z.enum(['SALE_AGREED', 'FOLLOW_UP_AGREED', 'NO_SALE', 'NOT_SALES']).optional(),
    /** Cursor from the previous page. */
    before: z.string().max(100).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const activityQuerySchema = z.object({
    period: z.enum(['day', 'week']).default('day'),
    from: isoDate.optional(),
    to: isoDate.optional(),
    regionId: z.uuid().optional(),
});
const modelName = z.string().trim().regex(/^[A-Za-z0-9._/-]{1,200}$/, 'Use a model name, e.g. large-v3, or a Hugging Face repository');
/** A corrected transcript line: what was said. Empty when nobody spoke (the model made it up). */
export const transcriptLineSchema = z.object({ text: z.string().trim().max(2000, 'At most 2000 characters') });
export const transcriptionSettingsSchema = z
    .object({
    mode: z.enum(['LOCAL', 'HOSTED', 'OFF']),
    /** For all languages (e.g. turbo); null: the server's default. */
    model: modelName.nullable().default(null),
    models: z.partialRecord(languageSchema, modelName),
    vocabulary: z.array(z.string().trim().min(1).max(60)).max(200),
    hostedUrl: z.url({ protocol: /^https$/ }).max(500).nullable(),
    hostedModel: z.string().trim().max(120).nullable(),
    /** Write-only. Null keeps the stored key. */
    hostedKey: z.string().max(500).nullable(),
    /** Left out: unchanged. */
    trainingClipDays: z.number().int().min(0, 'At least 0').max(3650, 'At most 3650 days (10 years)').optional(),
})
    .superRefine((value, ctx) => {
    if (value.mode === 'HOSTED' && !value.hostedUrl)
        ctx.addIssue({ code: 'custom', path: ['hostedUrl'], message: 'Enter the service’s address' });
});
// ---- AI call analysis ---------------------------------------------------------------
export const saleOutcomes = ['SALE_AGREED', 'FOLLOW_UP_AGREED', 'NO_SALE', 'NOT_SALES'];
export const outcomeCorrectionSchema = z.object({ outcome: z.enum(saleOutcomes) });
export const reasonsCorrectionSchema = z.object({ categoryIds: z.array(z.uuid()).max(10) });
export const findingFeedbackSchema = z.object({ note: z.string().trim().max(500).nullable() });
export const reasonCategorySchema = z.object({
    name: z.string().trim().min(1, 'Enter a name').max(60),
    description: z.string().trim().max(300),
    status: z.enum(['ACTIVE', 'SUGGESTED', 'REJECTED', 'ARCHIVED']),
});
const criterionSchema = z.object({
    key: z.string().regex(/^[a-z0-9-]{1,40}$/),
    name: z.string().trim().min(1, 'Enter a name').max(60),
    description: z.string().trim().max(300),
});
export const analysisSettingsSchema = z
    .object({
    mode: z.enum(['OFF', 'ANTHROPIC', 'OPENAI', 'OPENAI_COMPATIBLE']),
    model: z.string().trim().max(120).nullable(),
    baseUrl: z.url({ protocol: /^https?$/ }).max(500).nullable(),
    /** Write-only. Null keeps the stored key. */
    key: z.string().max(500).nullable(),
    language: languageSchema,
    maskBeforeSending: z.boolean(),
    maskTranscriptsFor: z.enum(['NONE', 'AGENTS', 'NON_ADMINS']),
    minReviewSeconds: wholeNumber(0, 3600, 'seconds'),
    conversationDays: wholeNumber(1, 60, 'days'),
    scorecard: z.array(criterionSchema).max(12),
})
    .superRefine((value, ctx) => {
    if (value.mode === 'OPENAI_COMPATIBLE' && !value.baseUrl)
        ctx.addIssue({ code: 'custom', path: ['baseUrl'], message: 'Enter the endpoint’s address' });
    if ((value.mode === 'OPENAI_COMPATIBLE' || value.mode === 'OPENAI') && !value.model)
        ctx.addIssue({ code: 'custom', path: ['model'], message: 'Enter the model name' });
});
export const insightQuerySchema = z.object({
    from: isoDate.optional(),
    to: isoDate.optional(),
    regionId: z.uuid().optional(),
    userId: z.uuid().optional(),
    /** One of the tenant's numbers (the line called or called from). */
    phoneNumberId: z.uuid().optional(),
});
// ---- Recording notice ------------------------------------------------------------
export const recordingNoticeTextSchema = z.object({ text: z.string().trim().max(500).nullable() });
export const announcementTypes = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/ogg', 'audio/mp4', 'audio/x-m4a', 'audio/webm'];
export const maxAnnouncementBytes = 5 * 1024 * 1024;
export const pauseRecordingSchema = z.object({ paused: z.boolean() });
